import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useFormik } from 'formik'
import pullAllBy from 'lodash/pullAllBy'
import * as Yup from 'yup'

import PlusOutlinedIcon from '@ant-design/icons/PlusOutlined'
import PriorityIcon from '@icons/Priority'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { useMe } from '@shared/providers/src/MeProvider'
import { TaskKind, TaskPriority, TaskStatus, UserRole } from '@shared/utils'
import { TreatmentType } from '@shared/utils/src/Constants'

import { inputContainer } from '@utils/StylesHelper'
import InputControl from '@components/InputControl'
import SelectPrescription from '@components/SelectPrescription'
import SelectUser from '@components/SelectUser'

import { useDiagnosis, useTaskCreation } from './NewTask.hooks'

export function NewTaskButton({ tasks = [], hideMe = false, assigneeRole }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)} startIcon={<PlusOutlinedIcon />} sx={{ minWidth: 120 }}>
        Add Task
      </Button>
      <NewTaskDialog tasks={tasks} assigneeRole={assigneeRole} hideMe={hideMe} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export function NewTaskDialog({
  open,
  patient,
  tasks = [],
  hideMe = false,
  assigneeRequired = false,
  assigneeRole,
  presetType,
  relatedTaskId,
  title,
  onSuccess,
  onClose,
}) {
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} scroll="body">
      <Form
        hideMe={hideMe}
        tasks={tasks}
        assigneeRequired={assigneeRequired}
        assigneeRole={assigneeRole}
        patient={patient}
        presetType={presetType}
        relatedTaskId={relatedTaskId}
        title={title}
        onSuccess={onSuccess}
        onClose={onClose}
      />
    </Dialog>
  )
}

function Form({
  patient = null,
  tasks = [],
  hideMe = false,
  assigneeRequired = false,
  assigneeRole,
  presetType,
  relatedTaskId,
  title = 'Add New Task',
  onSuccess,
  onClose,
}) {
  const [assignedToMe, setAssignedToMe] = useState(false)

  const me = useMe()
  const tasksPreventManualCreation = useLookup(Lookup.TasksPreventManualCreation)
  const rxRequiredTypes = useLookup(Lookup.RXRequiredTasks)
  const qLinkTaskPanels = useLookup(Lookup.QLinkTaskPanels)
  const tasksOptionalPatients = useLookup(Lookup.TasksOptionalPatient, (tasks) => tasks.map((t) => t.kind))

  const isRxRequired = (kind) => rxRequiredTypes.some((t) => t.kind === kind)
  const qLinkDataRequired = (type) => type === TaskKind.QLinkLab
  const isPatientOptional = (type) => tasksOptionalPatients.includes(type)

  const createTask = useTaskCreation()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: presetType || '',
      priority: TaskPriority.Medium,
      patient,
      assigned: presetType === TaskKind.QLinkLab ? (me.provider?.assistant ?? null) : null,
      prescription: null,
      qLinkPanels: [],
      qLinkDiagnosis: [],
    },
    validationSchema: Yup.object({
      type: Yup.string().required('Type is required'),
      priority: Yup.string().required('Priority is required'),
      patient: Yup.object()
        .when('type', {
          is: (type) => !isPatientOptional(type),
          then: (schema) => schema.required('Patient is required'),
        })
        .nullable(),
      assigned: assigneeRequired ? Yup.object().required('Assignee is required').nullable() : Yup.object().nullable(),
      prescription: Yup.object().nullable(),
      qLinkPanels: Yup.array()
        .of(Yup.string())
        .when('type', {
          is: qLinkDataRequired,
          then: (schema) => schema.min(1, 'At least one panel is required'),
        }),
      qLinkDiagnosis: Yup.array()
        .of(Yup.object())
        .when('type', {
          is: qLinkDataRequired,
          then: (schema) => schema.min(1, 'At least one diagnosis is required'),
        }),
    }),
    onSubmit: (values) => {
      const assigneeId = assignedToMe ? me.id : values.assigned?.id

      return createTask
        .mutateAsync({
          task: {
            ...(Boolean(values.patient) && { patient_id: values.patient.id }),
            assignee_id: assigneeId,
            kind: values.type,
            priority: values.priority,
            status: TaskStatus.ToDo,
          },
          ...(isRxRequired(values.type) && Boolean(values.prescription) && { prescription_id: values.prescription.id }),
          ...(qLinkDataRequired(values.type) &&
            Boolean(values.qLinkPanels.length) && {
              panels: values.qLinkPanels,
            }),
          ...(qLinkDataRequired(values.type) &&
            Boolean(values.qLinkDiagnosis.length) && {
              diagnosis: values.qLinkDiagnosis.map((d) => d.label),
            }),
          ...(relatedTaskId && { related_task_id: relatedTaskId }),
        })
        .then(() => {
          toast.success('Task created')
          onClose()
          onSuccess?.()
        })
    },
  })

  const { data: diagnosisOptions = [], isPending: isDiagnosisPending } = useDiagnosis({ enabled: qLinkDataRequired(formik.values.type) })

  const allowedTasks = useMemo(() => {
    return pullAllBy(Array.from(tasks), tasksPreventManualCreation, 'kind')
  }, [tasks, tasksPreventManualCreation])

  const isAssigneeDisabled = assignedToMe || (formik.values.type === TaskKind.QLinkLab && Boolean(me.provider?.assistant))
  const isHIVPatient = Boolean(
    formik.values.patient?.treatmentType?.includes(TreatmentType.HIV) || formik.values.patient?.treatmentTypes?.includes(TreatmentType.HIV)
  )

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="type" formikProps={formik}>
              <TextField
                label="Task type"
                required
                fullWidth
                select
                disabled={Boolean(presetType)}
                onChange={(e) => {
                  const newType = e.target.value
                  if (formik.values.type === TaskKind.QLinkLab && newType !== TaskKind.QLinkLab) {
                    formik.setFieldValue('assigned', null)
                  }
                  if (newType === TaskKind.QLinkLab && me.provider?.assistant) {
                    formik.setFieldValue('assigned', me.provider?.assistant)
                  }
                  if (isRxRequired(newType) && formik.values.prescription) {
                    formik.setFieldValue('prescription', null)
                  }
                  formik.setFieldValue('type', newType)
                }}
              >
                {presetType && <MenuItem value={presetType}>{presetType}</MenuItem>}
                {allowedTasks.map(({ kind, label }) => {
                  const qLinkDisabled = kind === TaskKind.QLinkLab && !isHIVPatient
                  return (
                    <MenuItem key={kind} value={kind} disabled={qLinkDisabled}>
                      {qLinkDisabled ? (
                        <Stack>
                          <Typography variant="body2">{label}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Only available for HIV patients
                          </Typography>
                        </Stack>
                      ) : (
                        label
                      )}
                    </MenuItem>
                  )
                })}
              </TextField>
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputControl field="priority" formikProps={formik}>
              <TextField required fullWidth select>
                {Object.entries(TaskPriority).map(([label, value]) => (
                  <MenuItem key={value} value={value}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                      <PriorityIcon value={value} />
                      {label}
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </InputControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <Stack spacing={1}>
                <InputLabel error={formik.touched.patient && Boolean(formik.errors.patient)}>
                  Patient
                  {isPatientOptional(formik.values.type) && (
                    <Typography component="span" sx={{ color: 'text.secondary' }}>
                      {' '}
                      (optional)
                    </Typography>
                  )}
                </InputLabel>
                <SelectUser
                  value={formik.values.patient}
                  onChange={(user) => {
                    const nextIsHIVPatient = Boolean(user?.treatmentTypes?.includes('HIV'))
                    if (!nextIsHIVPatient && formik.values.type === TaskKind.QLinkLab) {
                      formik.setFieldValue('type', '')
                      formik.setFieldValue('assigned', null)
                      formik.setFieldValue('qLinkPanels', [])
                      formik.setFieldValue('qLinkDiagnosis', [])
                    }
                    formik.setFieldValue('patient', user)
                    formik.setFieldValue('prescription', null)
                  }}
                  error={formik.touched.patient && Boolean(formik.errors.patient)}
                  helperText={formik.touched.patient && formik.errors.patient}
                  role={UserRole.Patient}
                  disabled={Boolean(patient)}
                />
              </Stack>
              <Collapse in={isRxRequired(formik.values.type)}>
                <Stack spacing={1}>
                  <InputLabel error={formik.touched.prescription && Boolean(formik.errors.prescription)}>Prescription</InputLabel>
                  <SelectPrescription
                    patientId={formik.values.patient?.id}
                    value={formik.values.prescription}
                    onChange={(prescription) => formik.setFieldValue('prescription', prescription)}
                    error={formik.touched.prescription && Boolean(formik.errors.prescription)}
                    helperText={formik.touched.prescription && formik.errors.prescription}
                    placeholder="Select related Rx"
                  />
                </Stack>
              </Collapse>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <InputLabel error={formik.touched.assigned && Boolean(formik.errors.assigned)}>
                Assignee{assigneeRequired ? '' : ' (optional)'}
              </InputLabel>
              <SelectUser
                value={formik.values.assigned}
                onChange={(user) => formik.setFieldValue('assigned', user)}
                error={formik.touched.assigned && Boolean(formik.errors.assigned)}
                helperText={formik.touched.assigned && formik.errors.assigned}
                role={assigneeRole}
                disabled={isAssigneeDisabled}
              />
              {!hideMe && (
                <FormControlLabel
                  label="Assign to me"
                  control={
                    <Checkbox
                      checked={assignedToMe}
                      onChange={(e, newValue) => {
                        // Clear the assigned field if the user checks the checkbox
                        if (newValue && formik.values.assigned) formik.setFieldValue('assigned', null)
                        setAssignedToMe(newValue)
                      }}
                    />
                  }
                  labelPlacement="end"
                />
              )}
            </Stack>
          </Grid>
          {qLinkDataRequired(formik.values.type) && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1} sx={inputContainer(!formik.values.qLinkPanels.length)}>
                  <InputLabel htmlFor="qLinkPanels" error={formik.touched.qLinkPanels && Boolean(formik.errors.qLinkPanels)}>
                    Lab Panels
                  </InputLabel>
                  <Autocomplete
                    id="qLinkPanels"
                    multiple
                    fullWidth
                    disableCloseOnSelect
                    options={qLinkTaskPanels}
                    value={formik.values.qLinkPanels}
                    onChange={(event, newValue) => formik.setFieldValue('qLinkPanels', newValue)}
                    onBlur={formik.handleBlur}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        name="qLinkPanels"
                        variant="outlined"
                        error={formik.touched.qLinkPanels && Boolean(formik.errors.qLinkPanels)}
                        helperText={formik.touched.qLinkPanels && formik.errors.qLinkPanels}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1} sx={inputContainer(!formik.values.qLinkDiagnosis.length)}>
                  <InputLabel htmlFor="qLinkDiagnosis" error={formik.touched.qLinkDiagnosis && Boolean(formik.errors.qLinkDiagnosis)}>
                    Q Link Diagnosis
                  </InputLabel>
                  <Autocomplete
                    id="qLinkDiagnosis"
                    multiple
                    fullWidth
                    disableCloseOnSelect
                    options={diagnosisOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={formik.values.qLinkDiagnosis}
                    onChange={(event, newValue) => formik.setFieldValue('qLinkDiagnosis', newValue)}
                    onBlur={formik.handleBlur}
                    loading={isDiagnosisPending}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => <Chip {...getItemProps({ index })} key={option.code} label={option.code} />)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        name="qLinkDiagnosis"
                        variant="outlined"
                        error={formik.touched.qLinkDiagnosis && Boolean(formik.errors.qLinkDiagnosis)}
                        helperText={formik.touched.qLinkDiagnosis && formik.errors.qLinkDiagnosis}
                        slotProps={{
                          ...params.slotProps,
                          input: {
                            ...params.slotProps.input,
                            endAdornment: (
                              <>
                                {isDiagnosisPending ? <CircularProgress size={20} /> : null}
                                {params.slotProps.input.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    renderOption={({ key, ...props }, option) => (
                      <li key={option.code} {...props}>
                        <Stack>
                          <Typography>{option.code}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {option.label}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                  />
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={createTask.isPending} variant="contained" type="submit">
          Add
        </Button>
      </DialogActions>
    </form>
  )
}
